import React from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
import PullRequestsIcon from '@atlaskit/icon/glyph/bitbucket/pullrequests';
import { gridSize } from '@atlaskit/theme';
import Tooltip from '@atlaskit/tooltip';

import GenericMessage from 'src/components/generic-message';
import * as styles from 'src/components/pull-requests-table/pull-request-row.style';
import { useIntl } from 'src/hooks/intl';
import messages from 'src/sections/repository/components/prlist-card.i18n';
import { BranchPrListResponse } from 'src/sections/repository/sections/branch/hooks/branch-pr-list';
import { DetailedBranchPullRequest } from 'src/sections/repository/sections/branches/types';
import { ConnectedExpander } from 'src/sections/repository/sections/pull-request/components/connected-expander';
import StateLozenge from 'src/sections/repository/sections/pull-request/components/state-lozenge';

type PRListCardProps = {
  isSidebarCollapsed?: boolean;
  localStorageKey: string;
  createPrPath: string | undefined;
  branchPrList: BranchPrListResponse;
};

export const PRListCard = ({
  isSidebarCollapsed,
  localStorageKey,
  branchPrList,
}: PRListCardProps): JSX.Element => {
  const intl = useIntl();
  const icon = <PullRequestsIcon size="medium" label="prlist-sidecard" />;
  const { branch_prs, hasError, isLoading } = branchPrList;

  const labels = {
    rich: (
      <FormattedMessage
        {...messages.prCount}
        values={{
          total: branch_prs.length,
          prCount: <b>{branch_prs.length}</b>,
        }}
      />
    ),
    plain: intl.formatMessage(messages.prCount, {
      total: branch_prs.length,
      prCount: branch_prs.length,
    }),
  };

  if (isSidebarCollapsed) {
    return (
      <Tooltip position="left" content={labels.plain}>
        <Button appearance="subtle" iconBefore={icon} />
      </Tooltip>
    );
  }

  const SpanSection = styled.span({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginLeft: `${gridSize()}px`,
    marginBottom: `${gridSize()}px`,
  });

  const singlePrLine = (prElement: any) => {
    return (
      <div style={{ display: 'flex' }} key={prElement.id}>
        <StateLozenge
          pullRequestState={prElement.state}
          // COREX-10222
          pullRequestDraft={prElement.draft || false}
          pullRequestCreatedOn={prElement.created_on}
          pullRequestClosedOn={prElement.closed_on}
        />
        <SpanSection>
          <Tooltip content={prElement.title}>
            <styles.TooltipBoundaries>
              <styles.HeaderLink
                data-qa="pr-list-sidecard"
                title=""
                href={prElement.links.html.href}
                disableNativeTooltip
              >
                {`#${prElement.id} ${prElement.title}`}
              </styles.HeaderLink>
            </styles.TooltipBoundaries>
          </Tooltip>
        </SpanSection>
      </div>
    );
  };

  const errorMessage = () => {
    return (
      <GenericMessage
        iconType="warning"
        title={<FormattedMessage {...messages.prListErrorMessage} />}
      ></GenericMessage>
    );
  };

  const branchList = (branchPrs: DetailedBranchPullRequest[]) => {
    return branchPrs.map(singlePrLine);
  };

  return (
    <ConnectedExpander
      localStorageKey={localStorageKey}
      icon={icon}
      label={labels.rich}
      isLoading={isLoading}
      sectionAriaLabel={intl.formatMessage(messages.sectionAriaLabel)}
      headingAriaLabel={intl.formatMessage(messages.headingAriaLabel, {
        prCount: hasError ? 0 : branch_prs.length,
      })}
    >
      {hasError ? (
        errorMessage()
      ) : branch_prs.length === 0 ? (
        <span>{intl.formatMessage(messages.noPRs)}</span>
      ) : (
        branchList(branch_prs)
      )}
    </ConnectedExpander>
  );
};

export default React.memo(PRListCard);
